import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { createContext, useContext } from 'react';

/**
 * Represents values related to message placement.
 * @typedef {Object} MessagePlacementValues
 * @property {(callback: DismissCallback) => void} onMessageDismiss - Callback function for dismissing a message.
 * @property {Callback} dismissMessageFromPlacement - Callback for dismissing a message from placement.
 * @property {(callback: ReadCallback) => void} onMessageRead - Callback function for marking a message as read.
 * @property {Callback} markReadFromPlacement - Callback for marking a message as read.
 * @property {(callback: ViewedCallback) => void} onMessageViewed - Callback function for marking a message as viewed.
 * @property {Callback} markViewedFromPlacement - Callback for marking a message as viewed.
 * @property {(callback: ClickedCallback) => void} onMessageClicked - Callback function for marking a message as clicked.
 * @property {Callback} markClickedFromPlacement - Callback for marking a message as clicked.
 *
 */

/**
 * Creates a Publish-Subscribe pattern for managing callbacks.
 * @returns {Array<Subscribe, Publish>} An array containing a Subscribe function and a Publish function.
 */
var createPubSub = function createPubSub() {
  var subscribers = new Map();

  /**
   * Subscribes a callback function to the pub-sub system.
   * @param {Callback} subscriber - The callback function to subscribe.
   * @returns A function to unsubscribe the callback.
   */
  var subscribe = function subscribe(subscriber) {
    subscribers.set(subscriber, subscriber);
    return function () {
      subscribers.delete(subscriber);
    };
  };

  /**
   * Publishes the payload to all subscribed callbacks.
   * @param {Callback} payload - The payload to publish to the subscribers.
   */
  var publish = function publish(payload) {
    subscribers.forEach(function (cb) {
      return cb(payload);
    });
  };
  return [subscribe, publish];
};

/**
 * Creates the message placement values including callbacks for message dismiss and read.
 * @returns {MessagePlacementValues} The message placement values with callback functions.
 */
export var createMessagePlacementValues = function createMessagePlacementValues() {
  var _createPubSub = createPubSub(),
    _createPubSub2 = _slicedToArray(_createPubSub, 2),
    onMessageDismiss = _createPubSub2[0],
    dismissMessageFromPlacement = _createPubSub2[1];
  var _createPubSub3 = createPubSub(),
    _createPubSub4 = _slicedToArray(_createPubSub3, 2),
    onMessageRead = _createPubSub4[0],
    markReadFromPlacement = _createPubSub4[1];
  var _createPubSub5 = createPubSub(),
    _createPubSub6 = _slicedToArray(_createPubSub5, 2),
    onMessageViewed = _createPubSub6[0],
    markViewedFromPlacement = _createPubSub6[1];
  var _createPubSub7 = createPubSub(),
    _createPubSub8 = _slicedToArray(_createPubSub7, 2),
    onMessageClicked = _createPubSub8[0],
    markClickedFromPlacement = _createPubSub8[1];
  return {
    onMessageDismiss: onMessageDismiss,
    dismissMessageFromPlacement: dismissMessageFromPlacement,
    onMessageRead: onMessageRead,
    markReadFromPlacement: markReadFromPlacement,
    onMessageViewed: onMessageViewed,
    markViewedFromPlacement: markViewedFromPlacement,
    onMessageClicked: onMessageClicked,
    markClickedFromPlacement: markClickedFromPlacement
  };
};

/**
 * Context for message placement with callbacks for message dismiss and read.
 */
export var MessagePlacement = /*#__PURE__*/createContext(createMessagePlacementValues());

/**
 * Provider component for the message placement context.
 */
export var MessagePlacementProvider = MessagePlacement.Provider;

/**
 * Creates a MessagePlacementProvider component with the provided value.
 * @returns A functional component that wraps child components to pass down context.
 */
export var createMessagePlacementProvider = function createMessagePlacementProvider() {
  return function (_ref) {
    var children = _ref.children;
    return /*#__PURE__*/React.createElement(MessagePlacementProvider, {
      value: createMessagePlacementValues()
    }, children);
  };
};

/**
 * Custom hook that returns the context value for message placement.
 * @returns {MessagePlacementValues} -  context for message placement
 */
export var useMessagePlacementHooks = function useMessagePlacementHooks() {
  return useContext(MessagePlacement);
};